import React, {useCallback, useRef} from 'react'
import {useEditor, EditorContent} from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import TextAlign from '@tiptap/extension-text-align'
import ImageExtension from '@tiptap/extension-image'
import {Color} from '@tiptap/extension-color'
import TextStyle from '@tiptap/extension-text-style'
import Highlight from '@tiptap/extension-highlight'
import FontFamily from '@tiptap/extension-font-family'
import Superscript from '@tiptap/extension-superscript'
import Underline from '@tiptap/extension-underline'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import FontSize from './extensions/FontSize'
import MediaEmbed from './extensions/MediaEmbed'
import HtmlEmbed from './extensions/HtmlEmbed'
import Indent from './extensions/Indent'
import ObvioVariable from './extensions/ObvioVariable'
import CustomButton from './extensions/CustomButton'
import MenuBar from './MenuBar'
import {useUploadAsset} from 'lib/asset'
import {ToolbarItem, ObvioVariable as ObvioVariableType} from './types'
import {defaultToolbar} from './toolbar'
import useEditorTheme from './useEditorTheme'
import './html-editor.css'

export type HTMLEditorProps = {
  data: string
  onChange: (value: string) => void
  className?: string
  disabled?: boolean
  toolbar?: ToolbarItem[]
  customLinks?: string[]
  variables?: ObvioVariableType[]
}

export default function HTMLEditor(props: HTMLEditorProps) {
  const {data, onChange, className, disabled} = props
  const toolbar = props.toolbar || defaultToolbar
  const lastEmittedValue = useRef(data)
  const uploadAsset = useUploadAsset()
  const {theme, setTheme} = useEditorTheme()

  const handleImageUpload = useCallback(
    async (file: File) => {
      try {
        const asset = await uploadAsset(file)
        editor?.chain().focus().setImage({src: asset.file.url}).run()
      } catch (error) {
        // Upload failed - silently ignore
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4],
        },
      }),
      TextStyle,
      FontFamily,
      FontSize,
      Color,
      Highlight.configure({
        multicolor: true,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
        },
      }),
      ImageExtension.configure({
        inline: true,
      }),
      Superscript,
      Underline,
      Table.configure({
        resizable: false,
      }),
      TableRow,
      TableCell,
      TableHeader,
      MediaEmbed,
      HtmlEmbed,
      Indent,
      ObvioVariable,
      CustomButton,
    ],
    content: data || '',
    editable: !disabled,
    onUpdate: ({editor: updatedEditor}) => {
      const html = updatedEditor.getHTML()
      if (html !== lastEmittedValue.current) {
        lastEmittedValue.current = html
        onChange(html)
      }
    },
    editorProps: {
      handleDrop: (view, event) => {
        const files = event.dataTransfer?.files
        if (files && files.length > 0) {
          const imageFiles = Array.from(files).filter((f) =>
            f.type.startsWith('image/'),
          )
          if (imageFiles.length > 0) {
            event.preventDefault()
            imageFiles.forEach((file) => handleImageUpload(file))
            return true
          }
        }
        return false
      },
      handlePaste: (view, event) => {
        const files = event.clipboardData?.files
        if (files && files.length > 0) {
          const imageFiles = Array.from(files).filter((f) =>
            f.type.startsWith('image/'),
          )
          if (imageFiles.length > 0) {
            event.preventDefault()
            imageFiles.forEach((file) => handleImageUpload(file))
            return true
          }
        }
        return false
      },
    },
  })

  // Sync external data changes (e.g. form reset)
  React.useEffect(() => {
    if (!editor) {
      return
    }

    if (data !== lastEmittedValue.current) {
      lastEmittedValue.current = data
      editor.commands.setContent(data || '', false)
    }
  }, [data, editor])

  if (!editor) {
    return null
  }

  const themeClass =
    theme === 'Dark'
      ? 'html-editor--dark'
      : theme === 'Light'
      ? 'html-editor--light'
      : ''

  return (
    <div className={`html-editor ${themeClass} ${className || ''}`}>
      <MenuBar
        editor={editor}
        onImageUpload={handleImageUpload}
        toolbar={toolbar}
        variables={props.variables}
        customLinks={props.customLinks}
        theme={theme}
        onThemeChange={setTheme}
      />
      <EditorContent editor={editor} />
    </div>
  )
}
